import { useTranslation } from 'hooks';
import { useEffect, useState, useMemo } from 'react';
import CustomSelect from '../customSelect';
import { AddButton, DeleteButton } from './components';
import './multipleSelectList.scss';

export type Option = {
  label: string;
  value: string;
};

export const MultipleSelectionList: React.FC<{
  selectedValues: string[];
  options: Option[];
  onChange: (selected: Option[]) => void;
  disabled?: boolean;
  addSelectorButtonLabel?: string;
}> = ({
  selectedValues,
  options,
  onChange,
  disabled,
  addSelectorButtonLabel,
}) => {
  const getTranslate = useTranslation();
  const [selects, setSelects] = useState(selectedValues);

  const optionsMap = useMemo(
    () => new Map(options.map((opt) => [opt.value, opt])),
    [options]
  );

  useEffect(() => {
    onChange(
      selects
        .map((select) => {
          if (!select) return undefined;
          const option = optionsMap.get(select);
          return option || undefined;
        })
        .filter((option) => option !== undefined)
    );
  }, [selects]);

  return (
    <div className='multiple-select-list'>
      <div className='multiple-select-list__list-element'>
        <CustomSelect
          modern
          isDisabled={disabled}
          value={
            selects[0] && optionsMap.has(selects[0])
              ? {
                  label: optionsMap.get(selects[0])?.label,
                  value: selects[0],
                }
              : null
          }
          options={options.filter(
            (option) =>
              !selects.includes(option.value) || option.value === selects[0]
          )}
          onChange={({ value }) => {
            setSelects((selects) => {
              const newSelects = [...selects];
              newSelects[0] = value;
              return newSelects;
            });
          }}
        />
      </div>
      <div>
        {selects.slice(1).map((select, index) => (
          <div
            key={index}
            className='multiple-select-list__list-element_delitable'>
            <CustomSelect
              customClass='multiple-select-list__select_delitable'
              modern
              isDisabled={disabled}
              value={
                select && optionsMap.has(select)
                  ? {
                      label: optionsMap.get(select)?.label,
                      value: select,
                    }
                  : null
              }
              options={options.filter(
                (option) =>
                  !selects.includes(option.value) || option.value === select
              )}
              onChange={({ value }) => {
                setSelects((selects) => {
                  const newSelects = [...selects];
                  newSelects[index + 1] = value;
                  return newSelects;
                });
              }}
            />
            <DeleteButton
              disabled={disabled}
              onClick={() => {
                setSelects((selects) =>
                  selects.filter((_, i) => i !== index + 1)
                );
              }}
            />
          </div>
        ))}
      </div>
      {options.length > 1 && options.length > selects.length ? (
        <div
          onClick={() => {
            setSelects((selects) => [...selects, undefined]);
          }}>
          <AddButton disabled={disabled} />
          <div className='multiple-select-list__add-item-label'>
            {addSelectorButtonLabel ||
              getTranslate('components.multipleSelectionList.addItem')}
          </div>
        </div>
      ) : null}
    </div>
  );
};
